import fastSort from 'fast-sort'
import { path } from 'ramda'
import { ApartmentsShowingProps } from '../containers/Apartments/Apartments'
import { getApplicationsAmounts } from '../containers/Applications/values/handleApplicationsData'
import { selectFutureShowings } from '../selectors/showings'
import { Apartment, ApartmentStatus, Status } from '../types/apartments'
import { Application } from '../types/applications'
import { IndexedApplications, ShowingsInterface } from '../types/states'

const compareCreatedAtDates = (a: any, b: any) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()

const compareDate = (date1: string, date2: string) => {
  const date1Stamp: number = Date.parse(date1)
  const date2Stamp: number = Date.parse(date2)
  return date1Stamp - date2Stamp
}

const sortArrayItemsByDate = (flatArray: any[]) =>
  flatArray[0]
    ? flatArray.sort((arrayItem1: any, arrayItem2: any) =>
        compareDate(arrayItem1.startDate, arrayItem2.startDate)
      )
    : []
/**
 * Ascending order
 */
const compareStartDates = (a: any, b: any) =>
  new Date(a.startDate).getTime() - new Date(b.startDate).getTime()

const getAdminApartmentSortOrder = (status: Status) => {
  switch (status) {
    case ApartmentStatus.followUp:
      return 6
    case ApartmentStatus.rentContractWanted:
      return 5
    case ApartmentStatus.marketing:
      return 4
    case ApartmentStatus.confirmed:
      return 3
    case ApartmentStatus.created:
      return 2
    case ApartmentStatus.rented:
      return 1
    default:
      return 0
  }
}

const descSortByApartmentStatus = (status1: any, status2: any) => {
  if (
    getAdminApartmentSortOrder(status2) < getAdminApartmentSortOrder(status1)
  ) {
    return -1
  }
  if (
    getAdminApartmentSortOrder(status2) > getAdminApartmentSortOrder(status1)
  ) {
    return 1
  }
  return 0
}

const stableSortByStatus = (array: any[]) => {
  const stabilizedThis = array.map((item, index) => [item, index])
  stabilizedThis.sort((a, b) => {
    const order = descSortByApartmentStatus(
      path([0, 'status'], a),
      path([0, 'status'], b)
    )
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((item) => item[0])
}

const fastSortByCreatedApplications = (
  apartmentApplications: Application[]
) => {
  const applicationStatusAmounts = getApplicationsAmounts(apartmentApplications)
  return applicationStatusAmounts.created
}

const fastSortByAcceptedApplications = (
  apartmentApplications: Application[]
) => {
  const applicationStatusAmounts = getApplicationsAmounts(apartmentApplications)
  return applicationStatusAmounts.accepted
}

const fastSortByReservedShowingsAmount = (
  apartmentId: string,
  apartmentsShowingProps: ApartmentsShowingProps
) => {
  const apartmentShowingsInterface: ShowingsInterface =
    apartmentsShowingProps?.showingsInterfaces[apartmentId] || []
  const futureApartmentReservations = selectFutureShowings(
    apartmentShowingsInterface?.reservations || []
  )
  return futureApartmentReservations.length
}

const fastSortByReservableShowingsAmount = (
  apartmentId: string,
  apartmentsShowingProps: ApartmentsShowingProps
) => {
  const apartmentShowingsInterface: ShowingsInterface =
    apartmentsShowingProps?.showingsInterfaces[apartmentId] || []
  const reservables =
    apartmentShowingsInterface?.reservableShowings?.reservables || []
  const bestReservables =
    apartmentShowingsInterface?.reservableShowings?.bestReservables || []
  const reservableShowingsAmount = reservables.length + bestReservables.length
  return reservableShowingsAmount
}

/** Main sort order is apartment status, then we check if there are reserved showings and finally we sort apartments with invited applicants and then created */
const defaultSortAdminApartments = (
  apartments: Apartment[],
  indexedApplications: IndexedApplications,
  apartmentsShowingProps: ApartmentsShowingProps
) => {
  const sortedByCreatedApplications = fastSort(apartments).by({
    desc: (apartment) =>
      fastSortByCreatedApplications(
        indexedApplications[apartment.apartmentId] || []
      )
  })
  const sortedByAcceptedApplications = fastSort(sortedByCreatedApplications).by(
    {
      desc: (apartment) =>
        fastSortByAcceptedApplications(
          indexedApplications[apartment.apartmentId] || []
        )
    }
  )
  const sortedByShowingsReservedAmount = fastSort(
    sortedByAcceptedApplications
  ).by({
    desc: (apartment) =>
      fastSortByReservedShowingsAmount(
        apartment.apartmentId,
        apartmentsShowingProps
      )
  })
  const sortedByReservableShowings = fastSort(
    sortedByShowingsReservedAmount
  ).by({
    desc: (apartment) =>
      fastSortByReservableShowingsAmount(
        apartment.apartmentId,
        apartmentsShowingProps
      )
  })
  const sortedByApartmentStatus = stableSortByStatus(sortedByReservableShowings)
  return sortedByApartmentStatus
}

const orderObjectIntoSortArray = (informationOrder: string[], data: any) => {
  let ordered: any[][] = []
  informationOrder.forEach((key: string) => {
    ordered = [...ordered, [`${key}`, data[key]]]
  })
  return ordered
}

const returnTrueFirst = (item1: boolean | number, item2: boolean | number) =>
  Number(item2) - Number(item1)

const returnTrueFirstFromArray = (item1: any[], item2: any[]) => {
  const checked1 = item1[1] ? item1[1] : false
  const checked2 = item2[1] ? item2[1] : false
  return Number(checked2) - Number(checked1)
}

const orderWithSortMethod = (
  sortMethod: any,
  informationOrder: string[],
  data: any
) => {
  const ordered = informationOrder.map((key: string) => data[key])
  return ordered.sort(sortMethod)
}

/** creates [key, value], sorts them with chosen method and returns the keys */
const getSortedKeyArray = (
  sortMethod: (arg: any, arg2: any) => any,
  informationOrder: string[],
  data: any
) => {
  const ordered = orderObjectIntoSortArray(informationOrder, data)
  const sorted = ordered.sort(returnTrueFirstFromArray)
  // return key
  return sorted.map((item) => item[0])
}

export {
  compareCreatedAtDates,
  sortArrayItemsByDate,
  compareStartDates,
  descSortByApartmentStatus,
  stableSortByStatus,
  defaultSortAdminApartments,
  returnTrueFirst,
  orderWithSortMethod,
  getSortedKeyArray
}
